import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Grid,
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import { SVGRenderer } from "echarts/renderers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { API } from "aws-amplify";

import { UserContext } from "../contexts";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import { createRules, normalizeOperator, parseSqlToFilters } from "../methods";
import QueryInfo from "../components/querybuilder/QueryInfo";
import useUndoRedo from "../hooks/useUndoRedo";

import {
  GetPatientCount,
  GetEncounterCount,
  GetMedicationCount,
  GetProcedureCount,
  GetLabCount,
  GetTopTenDiagnoses,
  GetTopTenMedications,
  GetTopTenProcedures,
  GetAgeDistribution,
  GetAdiScoreDistribution,
  GetGenderDistribution,
  GetPatientHeatmapByState,
  QueryPatientData,
  getSharedQueries,
  getUsersQueries,
} from "../services";

import AskAIModal from "../components/querybuilder/AskAIModal";
import AgentFlowLogic from "../components/querybuilder/AgentFlowLogic";
import { useDispatch, useSelector } from "react-redux";
import QueryResults from "../components/querybuilder/components/QueryResults";
import Counts from "../components/querybuilder/Counts";
import walden from "../themes/echarts/Walden";
import { applyEChartsTheme } from "../themes/echartsTheme";
import { useTheme } from "@mui/material/styles";
import ChartDataAccordion from "../components/querybuilder/ChartDataAccordion";
import ListDataAccordion from "../components/querybuilder/ListDataAccordion";
import { v4 as uuidv4 } from 'uuid';
import CustomSnackbar from "../components/Snackbar";
import { clearDatabaseSelection, selectDatabase, selectMetadata, setSelectedDatabase, selectQueryOptions } from "../features/settings/settingsSlice";
import { selectAIFilters, selectAgentFlowData, clearAIFlowData, clearAgentFlowData } from "../features/chatbot/chatbotSlice";
import { setSelectedAsset } from "../features/userAssets/userAssetsSlice";
import TopToolbar from "../components/querybuilder/TopToolbar";
import AnalysisModal from "../components/analysis/AnalysisModal";
import { BarChart3, Users } from "lucide-react";

const OPTIONAL_COUNT_KEYS = ["encounters", "medications", "procedures", "labs"];

const RWEAIQueryBuilder = () => {
  const theme = useTheme();
  //contexts
  const {
    userAppSettings,
    setUserAppSettings,
    myQueries,
    setMyQueries,
    sharedQueries,
    setSharedQueries,
    activeStep,
    setActiveStep,
  } = useContext(UserContext);
  //state
  const [entities, setEntities] = useState([]);
  const [displayFields, setDisplayFields] = useState([]);
  const [sorting] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({ open: false, severity: 'info', message: '' });
  const [queryInfo, setQueryInfo] = useState({
    title: "",
    tags: "",
    desc: "",
    isShareable: true,
    queryID: "",
  });
  const [showQueryInfo, setShowQueryInfo] = useState(true);
  const [errors, setErrors] = useState({
    title: {
      show: false,
      message: ""
    },
    database: {
      show: false,
      message: ""
    }
  });
  const [filters, setFilters] = useState([]);
  const [initialPatientCount, setInitialPatientCount] = useState(0);
  const [queryResultData, setQueryResultData] = useState({});
  const [chartData, setChartData] = useState({
    ageAndGender: {},
    adi: {},
    gender: {},
    patientDistribution: {},
  });
  const [listData, setListData] = useState({
    diagnosis: {},
    medications: {},
    procedures: {},
  });
  const [countData, setCountData] = useState({});
  const [partsLoaded, setPartsLoaded] = useState({
    queryResults: true,
    topTenDiagnosis: true,
    topTenMedications: true,
    topTenProcedures: true,
    ageAndGenderChart: true,
    adiChart: true,
    genderChart: true,
    patientDistributionMap: true,
    patientsCount: true,
    proceduresCount: true,
    encountersCount: true,
    labsCount: true,
    medicationsCount: true
  })
  const [openModal, setOpenModal] = useState({
    filter: false,
    subFilter: false,
    cpt: false,
    icd9: false,
    icd10: false,
    executeQuery: false,
    queryResults: false,
    aiChat: false,
    flow: false
  });
  const [queryObject, setQueryObject] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [showSql, setShowSql] = useState(false);
  const [sql, setSql] = useState("");
  const [fileName, setFileName] = useState(null);
  let navigate = useNavigate();
  const { queryId } = useParams();
  const [initialQuery, setInitialQuery] = useState({});
  const [isSqlDisabled, setIsSqlDisabled] = useState(true);
  const [isExistingQuery, setIsExistingQuery] = useState(false);
  const [hasLoadedQueryInfo, setHasLoadedQueryInfo] = useState(false);
  const {
    state,
    setState,
    undo,
    redo,
    clear,
    isUndoPossible,
    isRedoPossible,
  } = useUndoRedo({
    past: [],
    present: [],
    future: [],
  });
  const [isPatientCountLoading, setIsPatientCountLoading] = useState(false);
  const [patientCountAbortController, setPatientCountAbortController] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const selectedDatabase = useSelector(selectDatabase);
  const metadata = useSelector(selectMetadata);
  const selectedAsset = useSelector((store) => store.userAssets.selectedAsset);
  const queryOptions = useSelector(selectQueryOptions);
  const aiFilters = useSelector(selectAIFilters);
  const agentFlowData = useSelector(selectAgentFlowData);
  // The cohort is now a server-materialized transient table of patient keys:
  // the Cortex Lambda executes the agent's CTAS DDL and reports back only the
  // verified table name (agentTable) via a cohort_ready Lambda event, or an
  // error (cohortError) via cohort_error. The client only ever holds this
  // table name — never SQL.
  const agentTable = agentFlowData?.agent_table || null;
  const cohortError = agentFlowData?.cohort_error || null;
  const dispatch = useDispatch();

  // Add location hook to detect route changes
  const location = useLocation();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  echarts.use([SVGRenderer]);

  // Reset hasInitialized when leaving the QueryBuilder page
  useEffect(() => {
    if (location.pathname !== '/rwe-ai') {
      setHasInitialized(false);
    }
  }, [location.pathname]);

  // Legacy: expand query_filters from the old AI schema into the filter pipeline.
  useEffect(() => {
    if (!aiFilters?.length || !metadata?.queryItems) return;

    const filterMap = {};
    aiFilters.forEach((f) => {
      const queryItem =
        findKeyInObjectsArray(metadata.queryItems, f.key) ||
        findKeyInObjectsArray(metadata.queryItems, f.key?.toUpperCase()) ||
        findKeyInObjectsArray(metadata.queryItems, f.key?.toLowerCase());
      if (!queryItem) return;

      const col = queryItem.column;
      if (!filterMap[col]) {
        filterMap[col] = {
          id: uuidv4(),
          category: queryItem.category,
          friendlyName: queryItem.friendlyName,
          column: queryItem.column,
          key: queryItem.key,
          isChildFilterNumeric: queryItem.isChildFilterNumeric || false,
          combinator: f.combinator || 'or',
          rows: [],
        };
      }
      filterMap[col].rows.push({
        id: uuidv4(),
        field: queryItem.column,
        value: f.value,
        friendlyName: queryItem.friendlyName,
        operator: normalizeOperator(f.operator),
        key: queryItem.key,
        rows: [],
      });
    });

    const expandedFilters = Object.values(filterMap);
    if (expandedFilters.length > 0) {
      setFilters(expandedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiFilters]);

  useEffect(() => {
    return () => {
      setIsExistingQuery(false);
    };
  }, []);

  useEffect(() => {
    if (activeStep === 0 && !isExistingQuery) {
      setShowQueryInfo(true);
      setFilters([]);
      clear();
      setCountData({});
      setExpanded(null);
      clearListAndChartData();
      setQueryInfo({
        title: "",
        tags: "",
        desc: "",
        isShareable: true,
        queryID: "",
      });
      setFileName(null);
      setHasLoadedQueryInfo(false);
    } else if (activeStep === 0) {
      setShowQueryInfo(true);
    } else {
      setShowQueryInfo(false);
    }
  }, [activeStep, isExistingQuery]);

  useEffect(() => {
    if (Object.values(partsLoaded).every(x => x)) {
      setLoading(false);
    }
  }, [partsLoaded])

  useEffect(() => {
    let foundQuery = myQueries.find((x) => x.QueryID === queryId);
    if (foundQuery && !isExistingQuery) {
      setInitialPatientCount(foundQuery.DBPatientCount)
      setIsExistingQuery(true);
      setCountData(prevData => ({
        ...prevData,
        patients: {
          response: foundQuery.MostRecentPatientCount,
          "toolTipLabel": "Total Recorded Patients",
          "type": "count",
          "title": "Patients"
        }
      }));
      return;
    }

    if (filters !== undefined) {
      if (filters.length > 0) {
        if (!isSqlDisabled) {
          setIsSqlDisabled(true);
        }
        // When agentTable is present, the sql_statement effect below already
        // fetches Total Patients directly off it — skip here to avoid a
        // duplicate GetPatientCount call. This branch still runs for
        // non-agent cases (e.g. loading a saved query with existing filters).
        if (!agentTable) {
          const myInit = getQueryBodyObj();
          setCountData({});
          setExpanded(null);
          clearListAndChartData();
          fetchPatientData(myInit);
        }
      } else if (filters.length === 0 && activeStep !== 0 && !agentTable) {
        fetchAllData();
        setIsSqlDisabled(true);
      }
    }
  }, [filters]);

  useEffect(() => {
    if (countData.patients && countData.patients.response && filters.length === 0 && activeStep !== 0) {
      setInitialPatientCount(countData.patients.response);
    }
  }, [countData.patients, filters, activeStep]);

  useEffect(() => {
    if (selectedDatabase && metadata && selectedAsset) {
      const distinctCategories = [
        ...new Set(metadata?.queryItems?.map((item) => item.category)),
      ];
      const entitiesArr = [];
      distinctCategories.map((item, index) => {
        const foundEntity = metadata.queryItems.find(
          (entity) => entity.category === item && entity.icon
        ) || metadata.queryItems.find(
          (entity) => entity.category === item
        );
        entitiesArr.push({
          index,
          friendlyName: item,
          icon: foundEntity.icon,
          column: foundEntity.column,
          disabled: foundEntity?.disabled,
          visible: foundEntity?.visible,
          unstructured: foundEntity?.unstructured,
          enableTooltip: foundEntity?.enableTooltip,
          tooltip: foundEntity?.tooltip,
        });
      });

      //sorting entities logic below by splitting into 3 arrays and concatenating them back

      // Arrays to hold separated entities
      const notDisabledUnstructured = [];
      const unstructured = [];
      const disabled = [];
      const notVisible = [];

      // Separate entities into their respective arrays
      entitiesArr.forEach(entity => {
        if (entity.disabled && entity.visible === false) {
          notVisible.push(entity);
        } else if (entity.disabled) {
          disabled.push(entity);
        } else if (entity.unstructured) {
          unstructured.push(entity);
        } else {
          notDisabledUnstructured.push(entity);
        }
      });
      // Sort the notDisabledUnstructured array alphabetically by 'friendlyName'
      notDisabledUnstructured.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName));

      // Concatenate the arrays back into entitiesArr in the specified order
      const sortedEntities = notDisabledUnstructured.concat(unstructured, disabled);
      setEntities(sortedEntities);
    }
  }, [selectedDatabase, metadata, selectedAsset]);

  useEffect(() => {
    getSharedQueries(userAppSettings.id).then((data) => {
      if (data.success) {
        setSharedQueries(data.data);
      } else {
        setSharedQueries([]);
      }
    });
  }, []);

  useEffect(() => {
    // Prevent circular updates by checking if the state actually differs
    const currentStateString = JSON.stringify(state);
    const filtersString = JSON.stringify(filters);

    if (currentStateString !== filtersString) {
      setState(filters);
    }
  }, [filters, setState, state]);

  useEffect(() => {
    setHasLoadedQueryInfo(false);
  }, [queryId]);

  useEffect(() => {
    if (queryId && queryId !== "" && !hasLoadedQueryInfo) {
      // Check in myQueries
      let foundQuery = null;

      if (myQueries?.length > 0) {
        foundQuery = myQueries.find((x) => x.QueryID === queryId);
      }

      // If not found in myQueries, check in sharedQueries
      if (!foundQuery && sharedQueries?.length > 0) {
        foundQuery = sharedQueries.find((x) => x.QueryID === queryId);
      }

      if (foundQuery) {
        // Set the database and metadata FIRST
        if (foundQuery.DatabaseName) {
          dispatch(setSelectedDatabase(foundQuery.DatabaseName));
        }
        setIsExistingQuery(true);

        if (foundQuery?.QueryDefinition) {
          try {
            let queryData = JSON.parse(foundQuery.QueryDefinition);

            setQueryInfo({
              title: foundQuery?.QueryName || "",
              tags: Array.isArray(foundQuery?.Tags)
                ? foundQuery.Tags.join(", ")
                : (foundQuery?.Tags || ""),
              desc: foundQuery?.QueryDescription || "",
              definition: foundQuery?.QueryDefinition || "",
              queryID: foundQuery?.QueryID || "",
            });

            setHasLoadedQueryInfo(true);

            if (queryData?.displayFields) {
              queryData.displayFields.sort((a, b) =>
                a.friendlyName?.localeCompare(b.friendlyName)
              );
              setDisplayFields(queryData.displayFields);
            }

            if (queryData?.wheres) {
              setFilters(queryData.wheres);
              setState(queryData.wheres);
            }
          } catch (error) {
            console.error("Error parsing query definition:", error);
            setSnackbarInfo({
              open: true,
              message: "Error loading query: Invalid definition format",
              severity: "error"
            });
          }
        }
      } else if (queryId) {
        // Query ID provided but not found
        setSnackbarInfo({
          open: true,
          message: "Query not found",
          severity: "error"
        });
        navigate('/savedqueries');
      }
    }
  }, [myQueries, sharedQueries, queryId, navigate, hasLoadedQueryInfo]);

  const fetchInitialPatientCountForExistingQuery = async () => {
    if (!isExistingQuery || (initialPatientCount && initialPatientCount !== "0" && initialPatientCount !== 0)) return null;

    try {
      const myInit = {
        body: {
          dbType: metadata?.dbType,
          db: metadata?.db,
          wheres: [],
          email: userAppSettings.email,
        },
      };

      const value = await GetPatientCount(myInit);
      if (value?.results?.response) {
        const count = value.results.response;
        setInitialPatientCount(count);
        return count;
      }
    } catch (error) {
      console.error("Error fetching initial patient count for existing query:", error);
    }
    return null;
  };

  const getMyQueries = () => {
    getUsersQueries(userAppSettings.id).then((data) => {
      if (data.success) {
        setMyQueries(data.data);
      } else {
        setMyQueries([]);
      }
    });
  };

  const handleQueryInfoChange = (event, type) => {
    switch (type) {
      case "title":
        return setQueryInfo((prevState) => {
          return { ...prevState, title: event.target.value };
        });
      case "tags":
        return setQueryInfo((prevState) => {
          return { ...prevState, tags: event.target.value };
        });
      case "desc":
        return setQueryInfo((prevState) => {
          return { ...prevState, desc: event.target.value };
        });
      case "isShareable":
        return setQueryInfo((prevState) => {
          return { ...prevState, isShareable: event.target.checked };
        });
      default:
        return;
    }
  };

  const createQueryObj = () => {
    let queryID = undefined;

    if (
      queryObject?.queryID !== undefined &&
      queryObject?.queryID !== null &&
      queryObject?.queryID.length > 0
    ) {
      queryID = queryObject.queryID;
    } else if (
      queryInfo?.queryID !== undefined &&
      queryInfo?.queryID !== null &&
      queryInfo?.queryID.length !== 0
    ) {
      queryID = queryInfo.queryID;
    }

    const q = {
      queryID: queryID ?? userAppSettings.id + new Date().toISOString(),
      name: queryInfo.title,
      description: queryInfo.desc,
      tags: queryInfo.tags,

      queryData: JSON.stringify({ displayFields, wheres: filters }),
      isShareable: queryInfo.isShareable,
    };

    setQueryObject(q);

    return q;
  };

  const createUserSettingsObj = () => {
    const userData = {
      id: userAppSettings.id,
      email: userAppSettings.email,
      approvalStatus: userAppSettings.approvalStatus, // ,
    };
    return userData;
  };

  const handleSaveQuery = async (executedSQL, mostRecentPatientCount, dbPatientCount) => {
    if (queryInfo.title != "" && filters.length > 0) {
      try {
        const myInit = createSaveQueryBodyObj(executedSQL.replaceAll('\\', '\\\\').replace('"', '\\"'), mostRecentPatientCount, dbPatientCount);
        const result = await API.post(
          "CRCRestAPI",
          "/api/v1/saveQuery",
          myInit
        );

        if (result.success) {
          setFileName(result?.fileName);
        }
        setUserAppSettings(createUserSettingsObj());

        setSnackbarInfo({
          open: true,
          message: "Query Saved!",
          severity: "success",
        });
      } catch (ex) {
        console.error(ex);
        return setSnackbarInfo({
          open: true,
          message: "Error Saving Query!",
          severity: "error",
        });
      }
    } else {
      /*
      return setSnackbarInfo({
        open: true,
        message: "Cannot save empty query",
        severity: "error",
      });
      */
    }
  };

  const createSaveQueryBodyObj = (executedSQL, mostRecentPatientCount, dbPatientCount = null) => {
    const queryObject = createQueryObj();
    const userSettings = createUserSettingsObj();

    const myInit = {
      body: {
        queryID: queryObject?.queryID,
        userID: userSettings?.id,
        queryName: queryObject?.name,
        queryDescription: queryObject?.description,
        queryDefinition: queryObject?.queryData,
        tags: queryObject?.tags,
        isShareable: queryObject?.isShareable,
        email: userSettings?.email,
        db: metadata.db,
        isNew: true,
        executedSQL,
        mostRecentPatientCount: mostRecentPatientCount ?? 0,
        dbPatientCount: dbPatientCount || initialPatientCount || null
      },
    };

    return myInit;
  };

  // Every RWE-AI request carries the authoritative cohort subquery when one
  // is active. The standard QueryBuilder has an independent body builder.
  const getQueryBodyObj = () => {
    return createQueryBodyObj(
      displayFields,
      queryInfo.title,
      queryInfo.queryID,
      userAppSettings.email,
      null
    );
  }

  const createQueryBodyObj = (displayFields, queryName, queryID, email, filtersOverride = null) => {
    let createdQuery;
    if (filtersOverride) {
      createdQuery = createRules(filtersOverride);
    } else {
      createdQuery = createRules(filters);
    }

    const patientKey = metadata.patientKey;

    // starting with default data center key for fields to display
    let fieldsArr = [
      {
        name: "Data Center Key",
        field: patientKey,
        label: "Data Center Key",
      },
    ];

    if (displayFields === undefined) {
      displayFields = [];
    }

    setDisplayFields([...displayFields, ...fieldsArr]);
    const currentDisplayFields = [...displayFields, ...fieldsArr];
    const uniqueDisplayFields = [
      ...new Map(
        currentDisplayFields.map((v) => [JSON.stringify(v), v])
      ).values(),
    ];

    const myInit = {
      body: {
        dbType: selectedDatabase ? selectedDatabase.dbType : null,
        db: metadata ? metadata.db : null,
        modifiers: [
          {
            modifier: "TOP 100",
          },
        ],
        fields: [],
        wheres: {
          id: "g-0.8517528779532519",
          rules: createdQuery,
          combinator: "and",
          not: false,
        },
        orderBys: sorting,
        queryName: queryName,
        queryID: queryID,
        email: email,
        isNew: true,
        ...(agentTable ? { agentTable } : {}),
      },
    };

    return myInit;
  };



  const fetchPatientData = useCallback(async (myInit) => {
    if (patientCountAbortController) {
      patientCountAbortController.abort();
    }

    const newAbortController = new AbortController();
    setPatientCountAbortController(newAbortController);
    setIsPatientCountLoading(true);
    try {
      setPartsLoaded(prevData => ({ ...prevData, patientsCount: false }));
      const value = await GetPatientCount(myInit, newAbortController.signal);
      if (value) {
        setCountData(prevData => ({
          ...prevData,
          patients: value.results,
        }
        ));
      }
    } catch (exception) {
      if (exception.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.error({ exception });
        setCountData(prevData => ({
          ...prevData,
          patients: { response: "Retry" }
        }));
      }
    } finally {
      setIsPatientCountLoading(false);
      setPatientCountAbortController(null);
      setPartsLoaded(prevData => ({
        ...prevData,
        patientsCount: true
      }));
    }
  }, []);

  const fetchEncounterData = async (myInit) => {
    GetEncounterCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        encounters: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        encounters: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        encountersCount: true,
      }));
    });
  }

  const fetchMedicationData = async (myInit) => {
    GetMedicationCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        medications: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        medications: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        medicationsCount: true,
      }));
    });
  }

  const fetchProcedureData = async (myInit) => {
    GetProcedureCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        procedures: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        procedures: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        proceduresCount: true,
      }));
    });
  }

  const fetchLabData = async (myInit) => {
    GetLabCount(myInit).then((value) => {
      setCountData(prevData => ({
        ...prevData,
        labs: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setCountData(prevData => ({
        ...prevData,
        labs: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        labsCount: true,
      }));
    });
  }

  const fetchTopTenDiagnosisData = async (myInit) => {
    GetTopTenDiagnoses(myInit).then((value) => {
      setListData(prevData => ({
        ...prevData,
        diagnosis: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setListData(prevData => ({
        ...prevData,
        diagnosis: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        topTenDiagnosis: true,
      }));
    });
  }

  const fetchTopTenMedicationsData = async (myInit) => {
    GetTopTenMedications(myInit).then((value) => {
      setListData(prevData => ({
        ...prevData,
        medications: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setListData(prevData => ({
        ...prevData,
        medications: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        topTenMedications: true,
      }));
    });
  }

  const fetchTopTenProceduresData = async (myInit) => {
    GetTopTenProcedures(myInit).then((value) => {
      setListData(prevData => ({
        ...prevData,
        procedures: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setListData(prevData => ({
        ...prevData,
        procedures: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        topTenProcedures: true,
      }));
    });
  }

  const fetchAgeDistributionData = async (myInit) => {
    GetAgeDistribution(myInit).then((value) => {
      setChartData(prevData => ({
        ...prevData,
        ageAndGender: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        ageAndGender: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        ageAndGenderChart: true,
      }));
    });
  }

  const fetchAdiScoreDistributionData = async (myInit) => {
    GetAdiScoreDistribution(myInit).then((value) => {
      setChartData(prevData => ({
        ...prevData,
        adi: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        adi: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        adiChart: true,
      }));
    });
  }

  const fetchGenderDistributionData = async (myInit) => {
    GetGenderDistribution(myInit).then((value) => {
      setChartData(prevData => ({
        ...prevData,
        gender: value.results,
      }));
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        gender: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        genderChart: true,
      }));
    });

  }

  const fetchPatientHeatMapByStateData = async (myInit) => {
    GetPatientHeatmapByState(myInit).then((value) => {
      if (value.success) {
        setChartData(prevData => ({
          ...prevData,
          patientDistribution: value.results,
        }));

        const { usaJSON, specialAreas } = value.results;

        //specialAreas are the three states, alaska, puerto rico, and hawaii and their placement
        //usaJSON is the data needed by echarts since it no longer has these part of the library
        echarts.registerMap("USA", usaJSON, specialAreas);
      }
    }).catch((exception) => {
      console.error({ exception });
      setChartData(prevData => ({
        ...prevData,
        patientDistribution: { response: "Retry" },
      }));
    }).finally(() => {
      setPartsLoaded(prevData => ({
        ...prevData,
        patientDistributionMap: true,
      }));
    });
  }

  const fetchAllData = () => {
    const myInit = {
      body: {
        dbType: metadata.dbType,
        db: metadata.db,
        tableName: metadata.patientTableName,
        email: userAppSettings.email,
      },
    };
    setInitialQuery(myInit);
    // Always fetch patient data as it's required
    fetchPatientData(myInit);
    fetchCountsAndChartsData(myInit, null);
  }

  const clearListAndChartData = () => {
    setListData({
      diagnosis: {},
      medications: {},
      procedures: {},
    });

    setChartData({
      ageAndGender: {},
      adi: {},
      gender: {},
      patientDistribution: {},
    });

    setQueryResultData({});

    setCountData((prevData) => {
      const remaining = { ...prevData };
      OPTIONAL_COUNT_KEYS.forEach((key) => delete remaining[key]);
      return remaining;
    });
  }

  // complete reset function to ensure consistent state clearing
  const performCompleteReset = useCallback(() => {
    setActiveStep(0);
    setShowQueryInfo(true);
    setFilters([]);
    clear();
    setCountData({});
    setExpanded(null);
    clearListAndChartData();
    setQueryInfo({
      title: "",
      tags: "",
      desc: "",
      isShareable: true,
      queryID: "",
    });
    setIsExistingQuery(false);
    setHasLoadedQueryInfo(false);
    setFileName(null);

    setTimeout(() => {
      dispatch(clearDatabaseSelection());
      dispatch(setSelectedAsset(null));
      dispatch(clearAgentFlowData());
      dispatch(clearAIFlowData());
    }, 0);

    // Force QueryInfo to remount by changing its key
    setResetKey(prev => prev + 1);
  }, [setActiveStep, clear, dispatch]);

  // Reset query state when navigating to QueryBuilder (unless loading existing query)
  useEffect(() => {
    if (location.pathname === '/rwe-ai' && !queryId && !hasInitialized) {
      performCompleteReset();
      setHasInitialized(true);
    }
  }, [location.pathname, queryId, hasInitialized, performCompleteReset]);

  // Additional reset when activeStep is set to 0 from sidebar
  useEffect(() => {
    if (activeStep === 0 && !queryId && location.pathname === '/rwe-ai' && hasInitialized) {
      performCompleteReset();
    }
  }, [activeStep, queryId, location.pathname, hasInitialized, performCompleteReset]);

  // Parse SQL from the Cortex agent into filters so Run Query sends the same
  // criteria the agent used. Declared after the reset effects above so a
  // mount-time reset can't wipe freshly parsed filters (effects run in
  // declaration order within a commit).
  useEffect(() => {
    if (!metadata?.queryItems || !agentFlowData?.sql_statement) return;

    // The cohort is now a server-materialized transient table: if the Cortex
    // Lambda failed to build it (or never reported a table at all), there is
    // no authoritative cohort to run against. Hard-stop here rather than
    // silently falling back to best-effort parsed filters — those could
    // describe a very different (and larger) patient set than what the agent
    // actually verified, so we deliberately do not call setFilters below.
    if (cohortError || !agentTable) {
      setSnackbarInfo({
        open: true,
        message: `Could not build the AI cohort: ${cohortError || 'the agent did not return a cohort table'}. No results were run.`,
        severity: "error",
      });
      setCountData({});
      setExpanded(null);
      clearListAndChartData();
      return;
    }

    // Best-effort conversion for display only — every backend query filters
    // on the verified cohort table regardless of whether this conversion
    // into editable filters succeeds.
    const parsedFilters = parseSqlToFilters(agentFlowData.sql_statement, metadata);
    if (parsedFilters.length > 0) {
      setFilters(parsedFilters);
    } else {
      setSnackbarInfo({
        open: true,
        message: "Couldn't convert the AI-generated SQL into editable filters. Results will still use the verified AI cohort.",
        severity: "warning",
      });
    }

    // Fetch Total Patients immediately from the authoritative cohort table.
    const myInit = getQueryBodyObj();
    setInitialQuery(myInit);
    setCountData({});
    setExpanded(null);
    clearListAndChartData();
    fetchPatientData(myInit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFlowData?.sql_statement, agentFlowData?.agent_table, agentFlowData?.cohort_error, metadata?.queryItems]);

  //rest of the count & data fetches
  const fetchCountsAndChartsData = (myInit, dbPatientCount = null, isUserInitiated = false) => {
    clearListAndChartData();

    // Only fetch everything when it's an automatic (non-user-initiated) load with no filters.
    // When the user presses Run Query we always respect the queryOptions dropdown selection.
    const shouldFetchAllData = filters.length === 0 && !isExistingQuery && !isUserInitiated && !agentTable;
    const willFetchOptionalData = !isExistingQuery || isUserInitiated;

    // Set ALL loading flags upfront to prevent race condition
    setPartsLoaded(prev => ({
      ...prev,
      queryResults: false,
      ...(willFetchOptionalData && {
        encountersCount: (shouldFetchAllData || queryOptions.encounterCount) ? false : true,
        medicationsCount: (shouldFetchAllData || queryOptions.medicationCount) ? false : true,
        proceduresCount: (shouldFetchAllData || queryOptions.procedureCount) ? false : true,
        labsCount: (shouldFetchAllData || queryOptions.labCount) ? false : true,
        topTenDiagnosis: (shouldFetchAllData || queryOptions.top10Lists) ? false : true,
        topTenMedications: (shouldFetchAllData || queryOptions.top10Lists) ? false : true,
        topTenProcedures: (shouldFetchAllData || queryOptions.top10Lists) ? false : true,
        ageAndGenderChart: (shouldFetchAllData || queryOptions.distributionCharts) ? false : true,
        adiChart: (shouldFetchAllData || queryOptions.distributionCharts) ? false : true,
        genderChart: (shouldFetchAllData || queryOptions.distributionCharts) ? false : true,
        patientDistributionMap: (shouldFetchAllData || queryOptions.distributionMap) ? false : true,
      }),
    }));

    // Always fetch the essential patient data
    QueryPatientData(myInit).then(async (value) => {
      const patientDataResult = value.results;
      setSql(patientDataResult.patientDataQuery);
      setQueryResultData(patientDataResult);
      setCountData((prevData) => ({
        ...prevData,
        patients: {
          response: patientDataResult.patientCount,
          toolTipLabel: "Total Recorded Patients",
          type: "count",
          title: "Patients",
        },
      }));

      const patientDataQuery = patientDataResult.patientDataQuery;
      const queryWithoutLimits = patientDataQuery.substring(0, patientDataQuery.indexOf("LIMIT") - 1);
      const mostRecentPatientCount = Number(patientDataResult.patientCount.replaceAll(',', ''));

      const currentDbPatientCount = dbPatientCount || initialPatientCount;

      // Generate fileName for download functionality
      const generateFileName = () => {
        const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
        const baseName = queryInfo.title || 'query_results';
        return `${baseName}_${timestamp}.csv`;
      };

      // Only set fileName when user has built a query with filters (not for cached results)
      // This ensures Download button is only enabled after user runs a query
      if (filters.length > 0) {
        const generatedFileName = generateFileName();
        setFileName(generatedFileName);

        // Try to save the query (this will only save if title exists and filters exist)
        // If successful, the API may return a different fileName which will be set by handleSaveQuery
        await handleSaveQuery(queryWithoutLimits, mostRecentPatientCount, currentDbPatientCount);
      }

      // Only expand chartData accordion if distribution charts are selected or no filters (show all)
      const shouldExpandChartData = filters.length === 0 || queryOptions.distributionCharts;
      if (shouldExpandChartData) {
        setExpanded('chartData');
      }
    })
      .catch((exception) => {
        console.log({ exception });
      })
      .finally(() => {
        setPartsLoaded(prev => ({ ...prev, queryResults: true }));
      });

    if (isExistingQuery && !isUserInitiated) {
      return; // Don't auto-fetch expensive data when saved query first loads
    }

    // When no filters: always fetch everything (cached results - ignore localStorage)
    // When has filters: respect user's localStorage selections
    if (shouldFetchAllData) {
      // Fetch all data when no filters (cached results)
      fetchEncounterData(myInit);
      fetchMedicationData(myInit);
      fetchProcedureData(myInit);
      fetchLabData(myInit);
      fetchTopTenDiagnosisData(myInit);
      fetchTopTenMedicationsData(myInit);
      fetchTopTenProceduresData(myInit);
      fetchAgeDistributionData(myInit);
      fetchAdiScoreDistributionData(myInit);
      fetchGenderDistributionData(myInit);
      fetchPatientHeatMapByStateData(myInit);
    } else {
      // Fetch data based on user's selections when filters exist
      if (queryOptions.encounterCount) {
        fetchEncounterData(myInit);
      }
      if (queryOptions.medicationCount) {
        fetchMedicationData(myInit);
      }
      if (queryOptions.procedureCount) {
        fetchProcedureData(myInit);
      }
      if (queryOptions.labCount) {
        fetchLabData(myInit);
      }
      if (queryOptions.top10Lists) {
        fetchTopTenDiagnosisData(myInit);
        fetchTopTenMedicationsData(myInit);
        fetchTopTenProceduresData(myInit);
      }
      if (queryOptions.distributionCharts) {
        fetchAgeDistributionData(myInit);
        fetchAdiScoreDistributionData(myInit);
        fetchGenderDistributionData(myInit);
      }
      if (queryOptions.distributionMap) {
        fetchPatientHeatMapByStateData(myInit);
      }
    }
  }

  const getRetryQuery = (myInit) => {
    if (myInit?.body?.db && myInit?.body?.email) {
      return myInit;
    }

    if (initialQuery?.body?.db && initialQuery?.body?.email) {
      return initialQuery;
    }

    return getQueryBodyObj();
  };

  const fetchFunctions = {
    fetchPatientData: (myInit) =>
      fetchPatientData(getRetryQuery(myInit)),
    fetchEncounterData: (myInit) =>
      fetchEncounterData(getRetryQuery(myInit)),
    fetchMedicationData: (myInit) =>
      fetchMedicationData(getRetryQuery(myInit)),
    fetchProcedureData: (myInit) =>
      fetchProcedureData(getRetryQuery(myInit)),
    fetchLabData: (myInit) =>
      fetchLabData(getRetryQuery(myInit)),
    fetchTopTenDiagnosisData: (myInit) =>
      fetchTopTenDiagnosisData(getRetryQuery(myInit)),
    fetchTopTenMedicationsData: (myInit) =>
      fetchTopTenMedicationsData(getRetryQuery(myInit)),
    fetchTopTenProceduresData: (myInit) =>
      fetchTopTenProceduresData(getRetryQuery(myInit)),
    fetchAgeDistributionData: (myInit) =>
      fetchAgeDistributionData(getRetryQuery(myInit)),
    fetchAdiScoreDistributionData: (myInit) =>
      fetchAdiScoreDistributionData(getRetryQuery(myInit)),
    fetchGenderDistributionData: (myInit) =>
      fetchGenderDistributionData(getRetryQuery(myInit)),
    fetchPatientHeatMapByStateData: (myInit) =>
      fetchPatientHeatMapByStateData(getRetryQuery(myInit)),
  };

  const handleExecuteQuery = async () => {
    setLoading(true);

    // Fetch initial patient count for existing queries if needed
    let fetchedInitialPatientCount = null;
    if (isExistingQuery && (!initialPatientCount || initialPatientCount === "0" || initialPatientCount === 0)) {
      fetchedInitialPatientCount = await fetchInitialPatientCountForExistingQuery();
    }

    // Fetch other data while carrying the active AI cohort, when present.
    const myInit = getQueryBodyObj();
    setInitialQuery(myInit);

    // Clear the isExistingQuery flag when user actively runs a query
    if (isExistingQuery) {
      setIsExistingQuery(false);
    }

    // Pass true to indicate this is user-initiated, not automatic
    fetchCountsAndChartsData(myInit, fetchedInitialPatientCount || initialPatientCount, true);
    setIsSqlDisabled(false);
  };

  const handleNext = () => {
    let hasErrors = false;
    const newErrors = {
      title: { show: false, message: "" },
      database: { show: false, message: "" }
    };

    if (!selectedDatabase) {
      newErrors.database.show = true;
      newErrors.database.message = "Please select a database before proceeding.";
      hasErrors = true;
    }

    const foundQuery = myQueries?.find((q) => q.QueryName === queryInfo.title);
    if (foundQuery && showQueryInfo && foundQuery.QueryID !== queryInfo.queryID) {
      newErrors.title.show = true;
      newErrors.title.message = "Title already taken. Please enter unique title.";
      hasErrors = true;
    } else if (queryInfo.title === "" && showQueryInfo) {
      newErrors.title.show = true;
      newErrors.title.message = "Please add title before moving to next step.";
      hasErrors = true;
    }

    setErrors(newErrors);

    if (hasErrors) {
      return;
    }

    let allCount = 0;

    if (foundQuery !== undefined) {
      allCount = foundQuery.MostRecentPatientCount;
    }

    if (queryInfo.queryId && queryInfo.queryId != "") {
      if (myQueries?.length > 0) {
        setQueryInfo({
          queryID: foundQuery?.queryID,
          title: foundQuery?.name,
          tags: foundQuery?.tags,
          desc: foundQuery?.description,
        });

        if (foundQuery) {
          let queryData = JSON.parse(foundQuery?.QueryDefinition);
          queryData?.displayFields?.sort((a, b) =>
            a.friendlyName?.localeCompare(b.friendlyName)
          );

          setDisplayFields(queryData.displayFields);
          setFilters(queryData.wheres);
          setState(queryData.wheres);
        }
      }
    }
    setActiveStep(1);
    setShowQueryInfo(false);

    getMyQueries();
    //not sure why we are calling getDownloads call here, so commented it out
    //getDownloads();

    setLoading(true);
    //call to get initial results of patient summary
    if ((!filters || filters.length === 0) && !agentTable) {
      fetchAllData();
    } else {
      const myInit = getQueryBodyObj();
      fetchCountsAndChartsData(myInit, null);
    }
  };

  const handleBack = () => {
    setShowQueryInfo(true);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const findKeyInObjectsArray = (objectsArray, keyToFind) => {
    for (let i = 0; i < objectsArray.length; i++) {
      const currentObject = objectsArray[i];
      if (
        currentObject.key === keyToFind ||
        currentObject.column === keyToFind
      ) {
        return currentObject;
      }
      if (currentObject.childFilters) {
        const result = findKeyInObjectsArray(
          currentObject.childFilters,
          keyToFind
        );
        if (result) {
          return result;
        }
      }
    }
    return null;
  };

  const handleReset = () => {
    setDisplayFields([]);
    setFilters([]);
  };

  const handleUndo = () => {
    if (isUndoPossible) {
      const previousState = undo();
      if (previousState && JSON.stringify(previousState) !== JSON.stringify(filters)) {
        setFilters(previousState);
      }
    }
  };

  const handleRedo = () => {
    if (isRedoPossible) {
      const nextState = redo();
      if (nextState && JSON.stringify(nextState) !== JSON.stringify(filters)) {
        setFilters(nextState);
      }
    }
  };

  const handleOpenAIModal = () => {
    setOpenModal({ ...openModal, aiChat: true });
  };

  const handleCloseAIModal = () => {
    setOpenModal({ ...openModal, aiChat: false });
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (showQueryInfo) {
    return (
      <Box display={"flex"} justifyContent={"center"}>
        <QueryInfo
          key={resetKey}
          handleQueryInfoChange={handleQueryInfoChange}
          queryInfo={queryInfo}
          errors={errors}
          headerBackgroundColor={"#04aeea"}
          headerColor={"white"}
          handleNext={handleNext}
          userId={userAppSettings?.id}
          isEditMode={isExistingQuery}
        />
      </Box>
    );
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarInfo({ ...snackbarInfo, open: false });
  };

  const handleCopySQL = () => {
    navigator.clipboard.writeText(sql)
      .then(() => {
        setCopySuccess(true);
        setSnackbarInfo({
          open: true,
          message: "Copied SQL to clipboard",
          severity: "success",
        });
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy SQL query: ', err);
        setSnackbarInfo({
          open: true,
          message: "Failed to copy SQL to clipboard",
          severity: "error",
        });
      });
  };

  echarts.registerTheme("walden", walden);

  const patientCountValue = countData?.patients?.response;
  const hasPatientCount = typeof patientCountValue === "number";
  const queryMetadataSlot = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Users size={16} color="currentColor" />
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {isPatientCountLoading
          ? "Matching patients…"
          : hasPatientCount
            ? `${patientCountValue.toLocaleString()} patients matched`
            : "No results yet"}
      </Typography>
      {isPatientCountLoading && <CircularProgress size={14} thickness={5} />}
    </Box>
  );

  return (
    <Box
      display={"flex"}
      justifyContent={"center"}
      sx={{
        backgroundColor: 'surface.page',
        minHeight: '100vh',
        p: 2
      }}
    >
      <Grid container sx={{ width: "100%" }}>
        <Grid size={12}>
          <TopToolbar
            handleExecuteQuery={handleExecuteQuery}
            showEditActions={false}
            compact
            metadataSlot={queryMetadataSlot}
            metadata={metadata}
            filters={filters}
            sql={sql}
            entities={entities}
            fileName={fileName}
            db={metadata?.db}
            setSnackbarInfo={setSnackbarInfo}
            queryResultData={queryResultData}
            loading={loading}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            display: "flex",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              gap: 2,
            }}
          >
            {/* Query Results Panel */}
            <Paper
              elevation={1}
              sx={{
                flex: 3,
                minWidth: 0,
                p: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: "91vh",
                overflow: "auto",
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <BarChart3 size={20} color="currentColor" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Query Visualization
                </Typography>
              </Box>
              <Grid container spacing={1} justifyContent={"space-between"}>
                <Grid size={12}>
                  <Counts
                    countData={countData}
                    partsLoaded={partsLoaded}
                    fetchFunctions={fetchFunctions}
                    initialQuery={initialQuery}
                    queryOptions={queryOptions}
                  />
                </Grid>
                <Grid size={12}>
                  <ListDataAccordion
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    listData={listData}
                    fetchFunctions={fetchFunctions}
                    initialQuery={initialQuery}
                    partsLoaded={partsLoaded}
                  />
                  <ChartDataAccordion
                    expanded={expanded}
                    handleAccordionChange={handleAccordionChange}
                    chartData={chartData}
                    fetchFunctions={fetchFunctions}
                    initialQuery={initialQuery}
                    partsLoaded={partsLoaded}
                  />
                  <Accordion
                    expanded={expanded === "heatMap"}
                    onChange={handleAccordionChange("heatMap")}
                    disabled={Object.keys(chartData?.patientDistribution).length === 0}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      className="accordion-summary"
                    >
                      <Typography
                        variant="h6"
                        className="accordian-title"
                        fontSize={"1rem"}
                      >
                        Patient Distribution Map
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Paper
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "surface.raised",
                          boxShadow: (t) => t.palette.appShadow.card,
                        }}
                      >
                        <Box padding={2}>
                          <Typography
                            sx={{
                              fontSize: "0.87rem",
                              color: "text.secondary",
                              fontWeight: 400,
                            }}
                          >
                            Patient Distribution Map
                          </Typography>
                          {!loading && chartData?.patientDistribution?.response
                            ? (typeof chartData?.patientDistribution?.response === 'object'
                              ? <ReactECharts
                                option={applyEChartsTheme(chartData?.patientDistribution?.response, theme)}
                                opts={{ renderer: "svg" }}
                                theme={"walden"}
                              />
                              : <Button color="error" onClick={() => fetchFunctions.fetchPatientHeatMapByStateData()}>Retry</Button>
                            )
                            : <Button color="error" onClick={() => fetchFunctions.fetchPatientHeatMapByStateData()}>Retry</Button>
                          }
                        </Box>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid size={12}>
                  <QueryResults
                    resultData={queryResultData}
                    fetchFunctions={fetchFunctions}
                    initialQuery={initialQuery}
                    sql={sql}
                    fileName={fileName}
                    isLoading={loading}
                    db={metadata.db}
                    entities={entities}
                    isSqlDisabled={isSqlDisabled}
                    setSnackbarInfo={setSnackbarInfo}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Query Logic Panel */}
            <Paper
              elevation={1}
              sx={{
                flex: 2,
                minWidth: 0,
                p: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: "91vh",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Query Logic
              </Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <AgentFlowLogic
                  reactflowJson={agentFlowData?.reactflow_json}
                  cohortLogic={agentFlowData?.cohort_logic}
                  sqlStatement={agentFlowData?.sql_statement}
                />
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
      <AskAIModal
        openModal={openModal.aiChat}
        handleCloseModal={handleCloseAIModal}
        setSnackbarInfo={setSnackbarInfo}
        setFilters={setFilters}
        metadata={metadata}
      />
      <AnalysisModal queryResultData={queryResultData} />
      <CustomSnackbar
        severity={snackbarInfo.severity}
        message={snackbarInfo.message}
        open={snackbarInfo.open}
        handleClose={handleClose}
      />
    </Box>
  );
};

export default RWEAIQueryBuilder;
